import React, { useState, useMemo } from 'react'
import { ArrowLeft, BarChart3, Table, Trophy } from 'lucide-react'
import { useGame, usePresses } from '../hooks/useGames'
import { useMatch } from '../hooks/useMatches'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'
import { netScore } from '../utils/handicaps'
import { stablefordPoints } from '../utils/scoringEngine'
import { buildGameHandicapBreakdown, buildSideBetHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { useGameScoring, computePressHoleStatuses } from '../hooks/useGameScoring'
import { getScore as getScorePure, getStrokesOnHole as getStrokesOnHolePure, getTotal as getTotalPure, getNetTotal as getNetTotalPure, getScoreTaken as getScoreTakenPure, formatMatchDiff, formatPointDiff, teamAResultText } from '../utils/scorecardHelpers'
import RoundBanner from './RoundBanner'
import ScoreIndicator from './ScoreIndicator'
import GameTypeBadge from './GameTypeBadge'

export default function GameDetail({ gameId, matchId, onBack, initialView }) {
  const { data: game, isLoading } = useGame(gameId)
  const { data: match } = useMatch(matchId)
  const { data: presses } = usePresses(gameId)
  const [viewMode, setViewMode] = useState(initialView || 'leaders')

  if (isLoading) {
    return <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Loading...</p>
  }

  if (!game) {
    return (
      <div>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent-secondary)', minHeight: '48px' }}>
          <ArrowLeft size={18} /> Back
        </button>
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Game not found</p>
      </div>
    )
  }

  const matchPlayers = match?.match_players || []
  const players = game.game_players || []
  const scores = game.scores || []
  const numHoles = game.num_holes || 18
  const gameTeams = game.teams || []
  const course = getGameCourse(game)
  const tee = getGameTee(game)

  return (
    <div>
      {/* Header */}
      <button
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: 'var(--accent-secondary)',
          fontSize: '0.875rem',
          fontWeight: '500',
          marginBottom: '1rem',
          minHeight: '48px',
        }}
      >
        <ArrowLeft size={18} />
        Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>
            {game.game_name || game.course_name || 'Round Detail'}
          </h1>
          {game.game_type && game.game_type !== 'stroke' && (
            <GameTypeBadge gameTypeId={game.game_type} size="small" />
          )}
        </div>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
        {game.course_name && <span>{game.course_name} &middot; </span>}
        {new Date(game.completed_at || game.created_at).toLocaleDateString()} &middot; {numHoles} holes
        {game.wager > 0 && <span> &middot; ${Number(game.wager)}/game</span>}
        {tee && <span> &middot; {tee.name} tees</span>}
      </p>

      {/* View toggle */}
      <div style={{
        display: 'flex',
        gap: '0.375rem',
        marginBottom: '1rem',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '0.5rem',
        padding: '0.25rem',
      }}>
        <button
          onClick={() => setViewMode('leaders')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            padding: '0.5rem',
            borderRadius: '0.375rem',
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: viewMode === 'leaders' ? 'var(--text-primary)' : 'var(--text-muted)',
            backgroundColor: viewMode === 'leaders' ? 'var(--bg-secondary)' : 'transparent',
          }}
        >
          <BarChart3 size={14} />
          Leaders
        </button>
        <button
          onClick={() => setViewMode('scorecard')}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
            padding: '0.5rem',
            borderRadius: '0.375rem',
            fontSize: '0.8125rem',
            fontWeight: '600',
            color: viewMode === 'scorecard' ? 'var(--text-primary)' : 'var(--text-muted)',
            backgroundColor: viewMode === 'scorecard' ? 'var(--bg-secondary)' : 'transparent',
          }}
        >
          <Table size={14} />
          Scorecard
        </button>
      </div>

      {viewMode === 'leaders' ? (
        <LeadersView
          game={game}
          matchPlayers={matchPlayers}
          players={players}
          scores={scores}
          numHoles={numHoles}
          course={course}
          tee={tee}
          presses={presses}
        />
      ) : (
        <HorizontalScorecard
          game={game}
          players={players}
          scores={scores}
          numHoles={numHoles}
          course={course}
          tee={tee}
          matchPlayers={matchPlayers}
          gameTeams={gameTeams}
          gameType={game.game_type}
          presses={presses}
        />
      )}
    </div>
  )
}

function LeadersView({ game, matchPlayers, players, scores, numHoles, course, tee, presses }) {
  const totalPar = course ? course.par.reduce((s, p) => s + p, 0) : null

  const handicapBreakdown = useMemo(() => {
    const gameTeams = game.teams || []
    const teamStructure = gameTeams
      .sort((a, b) => a.team_order - b.team_order)
      .map((t) => ({ playerIds: (t.team_players || []).map((tp) => tp.player_id) }))
    return buildGameHandicapBreakdown({
      gameType: game.game_type, teamStructure, matchPlayers, gamePlayers: players, course, tee,
      mixedTees: getMixedTees(game),
    })
  }, [course, tee, matchPlayers, players, totalPar, game.game_type, game.teams])

  // Full course handicap breakdown (no game-type %, no off-low adjustment) for individual net scores
  const fullHandicapBreakdown = useMemo(() => {
    return buildSideBetHandicapBreakdown({
      matchPlayers, gamePlayers: players, course, tee, mixedTees: getMixedTees(game),
    })
  }, [course, tee, matchPlayers, players, totalPar])

  const getTotal = (playerId) => getTotalPure(scores, playerId)
  const getNetTotal = (playerId) => getNetTotalPure(scores, handicapBreakdown, playerId)

  // Full-handicap net total for individual scores (no game-type %, no off-low)
  const getFullNetTotal = (playerId) => {
    if (!fullHandicapBreakdown) return null
    const hcp = fullHandicapBreakdown.find((h) => h.playerId === playerId)
    if (!hcp) return null
    return scores
      .filter((sc) => sc.player_id === playerId && sc.strokes != null)
      .reduce((sum, sc) => {
        const strk = hcp.strokeHoles.get(sc.hole_number) || 0
        return sum + netScore(sc.strokes, strk)
      }, 0)
  }

  const getPlayerCourseHandicap = (playerId) => {
    const hcp = fullHandicapBreakdown?.find((h) => h.playerId === playerId)
    return hcp?.courseHandicap ?? null
  }

  return (
    <div>
      {/* Round banner — team standings */}
      <RoundBanner
        gameData={game}
        matchPlayers={matchPlayers}
        presses={presses}
      />

      {/* Divider */}
      <hr style={{
        border: 'none',
        borderTop: '1px solid var(--border-color)',
        margin: '0.75rem 0',
      }} />

      {/* Individual totals */}
      <h3 style={{
        fontSize: '1rem',
        fontWeight: '700',
        color: 'var(--text-primary)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '0.5rem',
      }}>
        Round Individual Scores
      </h3>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Player
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2rem', textAlign: 'right' }}>
              +/–
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
              Gross
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2rem', textAlign: 'right' }}>
              +/–
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '2.25rem', textAlign: 'right' }}>
              Net
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {players
            .map(({ player }) => player ? {
              player,
              total: getTotal(player.id),
              netTotal: getFullNetTotal(player.id),
            } : null)
            .filter(Boolean)
            .sort((a, b) => {
              const aVal = a.netTotal ?? a.total ?? 999
              const bVal = b.netTotal ?? b.total ?? 999
              return aVal - bVal
            })
            .map(({ player, total, netTotal }, index) => {
              const holesPlayed = scores.filter((sc) => sc.player_id === player.id && sc.strokes != null).length
              const parThrough = course && holesPlayed > 0
                ? scores
                    .filter((sc) => sc.player_id === player.id && sc.strokes != null)
                    .reduce((sum, sc) => sum + course.par[sc.hole_number - 1], 0)
                : null
              const grossToPar = parThrough != null && total > 0 ? total - parThrough : null
              const netToPar = parThrough != null && netTotal != null && netTotal > 0 ? netTotal - parThrough : null
              const courseHcp = getPlayerCourseHandicap(player.id)

              const formatToPar = (val) => val === 0 ? 'E' : val > 0 ? `+${val}` : val
              const toParColor = (val) => val === 0 ? 'var(--text-muted)' : val < 0 ? 'var(--accent-primary)' : 'var(--accent-danger)'

              return (
                <div
                  key={player.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 0',
                    borderBottom: index < players.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', width: '1.5rem' }}>
                      {index + 1}.
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '1.0625rem' }}>
                      {player.display_name}
                    </span>
                    {courseHcp != null && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        ({courseHcp})
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: '600', width: '2rem', textAlign: 'right', color: grossToPar != null ? toParColor(grossToPar) : 'var(--text-muted)' }}>
                      {grossToPar != null ? formatToPar(grossToPar) : ''}
                    </span>
                    <span style={{ fontSize: '0.9375rem', color: 'var(--text-muted)', width: '2.25rem', textAlign: 'right' }}>
                      {total || '–'}
                    </span>
                    <span style={{ fontSize: '0.9375rem', fontWeight: '600', width: '2rem', textAlign: 'right', color: netToPar != null ? toParColor(netToPar) : 'var(--text-muted)' }}>
                      {netToPar != null ? formatToPar(netToPar) : ''}
                    </span>
                    <span style={{ fontWeight: '800', fontSize: '1.25rem', width: '2.25rem', textAlign: 'right', color: netTotal != null && netTotal > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {(netTotal ?? total) || '–'}
                    </span>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

function HorizontalScorecard({ game, players, scores, numHoles, course, tee, matchPlayers, gameTeams, gameType, presses }) {
  // Use consolidated scoring hook
  const { handicapBreakdown, results: roundResults, matchPlayHoleStatus, strokePlayTeamScores, teamStructure } = useGameScoring(game, matchPlayers)

  const isStableford = gameType === 'stableford'
  const isSixPack = gameType === 'six_pack'
  const isHighLow = gameType === 'high_low'

  // Bind shared helpers to local context
  const getScore = (playerId, hole) => getScorePure(scores, playerId, hole)
  const getScoreTaken = (playerId, hole) => getScoreTakenPure(scores, playerId, hole)
  const getStrokesOnHole = (playerId, hole) => getStrokesOnHolePure(handicapBreakdown, playerId, hole)
  const getTotal = (playerId) => getTotalPure(scores, playerId)
  const getNetTotal = (playerId) => getNetTotalPure(scores, handicapBreakdown, playerId)

  // Group players by team
  const playersByTeam = useMemo(() => {
    if (gameTeams.length === 0) {
      return [{ name: null, players }]
    }
    return gameTeams
      .sort((a, b) => a.team_order - b.team_order)
      .map((team) => {
        const teamPlayerIds = (team.team_players || []).map((tp) => tp.player_id)
        const teamPlayers = players.filter((gp) => teamPlayerIds.includes(gp.player_id))
        return { name: team.team_name, players: teamPlayers }
      })
  }, [gameTeams, players])

  const hasTeams = playersByTeam.length > 1
  const teamColors = ['var(--accent-primary)', 'var(--accent-secondary, #f59e0b)', 'var(--accent-danger, #ef4444)', '#8b5cf6']

  const pressHoleStatuses = useMemo(() => {
    return computePressHoleStatuses(roundResults, presses, teamStructure)
  }, [roundResults, presses, teamStructure])

  const formatStrokeDiff = (diff) => {
    if (diff == null) return null
    if (diff === 0) return { text: 'E', color: 'var(--text-muted)' }
    if (diff > 0) return { text: `+${diff}`, color: 'var(--accent-primary)' }
    return { text: `${diff}`, color: 'var(--accent-danger, #ef4444)' }
  }

  const matchRowCount = matchPlayHoleStatus ? 2 : 0
  const pressRowCount = pressHoleStatuses.length
  const strokePlayRowCount = strokePlayTeamScores ? 1 : 0

  // Shared styles
  const stickyTeamCol = { padding: 0, position: 'sticky', left: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 2, width: '22px', minWidth: '22px' }
  const borderL = { borderLeft: '2px solid var(--border-color)' }

  return (
    <div style={{ overflowX: 'auto', marginBottom: '1.5rem', WebkitOverflowScrolling: 'touch', backgroundColor: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.25rem 0' }}>
      <table style={{
        borderCollapse: 'collapse',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
      }}>
        <thead>
          <tr>
            {hasTeams && (
              <th style={{ padding: 0, borderBottom: '1px solid var(--border-color)', position: 'sticky', left: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 3, width: '22px', minWidth: '22px' }} />
            )}
            <th style={{
              padding: '0.375rem 0.5rem', textAlign: 'left', color: 'var(--text-muted)', fontWeight: '600', fontSize: '0.625rem',
              borderBottom: '1px solid var(--border-color)', position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 2, minWidth: '64px',
            }}>
              Hole
            </th>
            {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => (
              <React.Fragment key={hole}>
                {numHoles === 18 && hole === 10 && (
                  <th style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', fontSize: '0.625rem', borderBottom: '1px solid var(--border-color)', ...borderL, minWidth: '34px' }}>
                    OUT
                  </th>
                )}
                <th style={{
                  padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border-color)', minWidth: '32px',
                  ...(numHoles === 18 && hole === 10 ? borderL : {}),
                }}>
                  {hole}
                </th>
              </React.Fragment>
            ))}
            {numHoles === 18 && (
              <th style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', fontSize: '0.625rem', borderBottom: '1px solid var(--border-color)', ...borderL, minWidth: '34px' }}>
                IN
              </th>
            )}
            <th style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--accent-primary)', fontSize: '0.6875rem', borderBottom: '2px solid var(--accent-primary)', ...borderL, minWidth: '36px' }}>
              TOT
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Par row */}
          {course && (
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              {hasTeams && <td style={stickyTeamCol} />}
              <td style={{
                padding: '0.375rem 0.5rem', fontWeight: '600', fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase',
                position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1,
              }}>
                Par
              </td>
              {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => (
                <React.Fragment key={hole}>
                  {numHoles === 18 && hole === 10 && (
                    <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)', fontSize: '0.6875rem', ...borderL }}>
                      {course.par.slice(0, 9).reduce((s, p) => s + p, 0)}
                    </td>
                  )}
                  <td style={{
                    padding: '0.375rem 0.25rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.6875rem',
                    ...(numHoles === 18 && hole === 10 ? borderL : {}),
                  }}>
                    {course.par[hole - 1]}
                  </td>
                </React.Fragment>
              ))}
              {numHoles === 18 && (
                <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '600', color: 'var(--text-muted)', fontSize: '0.6875rem', ...borderL }}>
                  {course.par.slice(9, 18).reduce((s, p) => s + p, 0)}
                </td>
              )}
              <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', fontWeight: '700', color: 'var(--text-muted)', ...borderL }}>
                {course.par.slice(0, numHoles).reduce((s, p) => s + p, 0)}
              </td>
            </tr>
          )}

          {/* Player rows grouped by team */}
          {playersByTeam.map((teamGroup, teamIdx) => {
            const tColor = teamColors[teamIdx % teamColors.length]
            const matchExtra = teamIdx === 0 ? matchRowCount + pressRowCount : 0
            const diffRowCount = teamIdx === 0 && strokePlayTeamScores?.diffs ? 2 : 0
            const extraRows = matchExtra + strokePlayRowCount + diffRowCount
            return (
              <React.Fragment key={teamGroup.name || teamIdx}>
                {teamGroup.players.map(({ player }, playerIdx) => {
                  if (!player) return null
                  const gross = getTotal(player.id)
                  const netTot = getNetTotal(player.id)

                  const front9Scores = scores.filter((sc) => sc.player_id === player.id && sc.hole_number >= 1 && sc.hole_number <= 9 && sc.strokes != null)
                  const front9Gross = front9Scores.reduce((sum, sc) => sum + sc.strokes, 0)
                  const front9Net = handicapBreakdown
                    ? front9Scores.reduce((sum, sc) => sum + netScore(sc.strokes, getStrokesOnHole(player.id, sc.hole_number)), 0)
                    : null
                  const back9Scores = scores.filter((sc) => sc.player_id === player.id && sc.hole_number >= 10 && sc.hole_number <= 18 && sc.strokes != null)
                  const back9Gross = back9Scores.reduce((sum, sc) => sum + sc.strokes, 0)
                  const back9Net = handicapBreakdown
                    ? back9Scores.reduce((sum, sc) => sum + netScore(sc.strokes, getStrokesOnHole(player.id, sc.hole_number)), 0)
                    : null

                  const showNetFront = front9Net != null && front9Net !== front9Gross
                  const showNetBack = back9Net != null && back9Net !== back9Gross
                  const showNetTotal = netTot != null && netTot !== gross

                  return (
                    <tr key={player.id} style={{
                      borderBottom: '1px solid var(--border-color)',
                      ...(hasTeams && teamIdx > 0 && playerIdx === 0 ? { borderTop: '2px solid var(--border-color)' } : {}),
                    }}>
                      {hasTeams && playerIdx === 0 && (
                        <td
                          rowSpan={teamGroup.players.length + extraRows}
                          style={{
                            position: 'sticky', left: 0, backgroundColor: 'var(--bg-secondary)', zIndex: 2,
                            width: '22px', minWidth: '22px', maxWidth: '22px',
                            writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center',
                            fontWeight: '800', fontSize: '0.5625rem', color: tColor, textTransform: 'uppercase',
                            letterSpacing: '0.1em', padding: '0.375rem 0.125rem', borderRight: `2px solid ${tColor}`,
                          }}
                        >
                          {teamGroup.name}
                        </td>
                      )}
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '600', fontSize: '0.6875rem', color: player.avatar_color,
                        position: 'sticky', left: hasTeams ? 22 : 0, backgroundColor: 'var(--bg-secondary)', zIndex: 1,
                      }}>
                        {player.display_name.split(' ')[0]}
                      </td>
                      {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                        const s = getScore(player.id, hole)
                        const strk = getStrokesOnHole(player.id, hole)
                        const net = s != null ? netScore(s, strk) : null
                        const hasStroke = strk > 0
                        const scoreTaken = isSixPack ? getScoreTaken(player.id, hole) : null
                        const holePar = course?.par[hole - 1]

                        return (
                          <React.Fragment key={hole}>
                            {numHoles === 18 && hole === 10 && (
                              <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', ...borderL }}>
                                <div style={{ fontWeight: '700', fontSize: '0.6875rem', color: 'var(--text-primary)' }}>
                                  {front9Gross || '–'}
                                </div>
                                {showNetFront && front9Gross > 0 && (
                                  <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{front9Net}</div>
                                )}
                              </td>
                            )}
                            <td
                              style={{
                                padding: '0.25rem 0.125rem', textAlign: 'center',
                                backgroundColor: hasStroke ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'transparent',
                                opacity: isSixPack && scoreTaken === false ? 0.35 : 1,
                                ...(numHoles === 18 && hole === 10 ? borderL : {}),
                              }}
                            >
                              <ScoreIndicator gross={s} net={net} par={holePar} />
                              {isStableford && s != null && holePar != null && (
                                <div style={{ fontSize: '0.5rem', fontWeight: '700', color: 'var(--accent-secondary, #f59e0b)', lineHeight: 1 }}>
                                  {stablefordPoints(net, holePar)}pt
                                </div>
                              )}
                            </td>
                          </React.Fragment>
                        )
                      })}
                      {numHoles === 18 && (
                        <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', ...borderL }}>
                          <div style={{ fontWeight: '700', fontSize: '0.6875rem', color: 'var(--text-primary)' }}>
                            {back9Gross || '–'}
                          </div>
                          {showNetBack && back9Gross > 0 && (
                            <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{back9Net}</div>
                          )}
                        </td>
                      )}
                      <td style={{ padding: '0.375rem 0.25rem', textAlign: 'center', ...borderL }}>
                        <div style={{ fontWeight: '800', fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                          {gross || '–'}
                        </div>
                        {showNetTotal && gross > 0 && (
                          <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', lineHeight: 1 }}>{netTot}</div>
                        )}
                      </td>
                    </tr>
                  )
                })}

                {/* Stroke play team total row (Stableford / Six Pack) */}
                {strokePlayTeamScores && (
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <td style={{
                      padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: tColor,
                      textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                      backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                    }}>
                      Total
                    </td>
                    {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                      const val = strokePlayTeamScores.running[teamIdx]?.[hole]
                      return (
                        <React.Fragment key={hole}>
                          {numHoles === 18 && hole === 10 && (
                            <td style={{
                              padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                              color: tColor, ...borderL,
                            }}>
                              {strokePlayTeamScores.front[teamIdx] || '–'}
                            </td>
                          )}
                          <td style={{
                            padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '700',
                            color: val != null ? tColor : 'transparent',
                            ...(numHoles === 18 && hole === 10 ? borderL : {}),
                          }}>
                            {val != null ? val : ''}
                          </td>
                        </React.Fragment>
                      )
                    })}
                    {numHoles === 18 && (
                      <td style={{
                        padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                        color: tColor, ...borderL,
                      }}>
                        {strokePlayTeamScores.back[teamIdx] || '–'}
                      </td>
                    )}
                    <td style={{
                      padding: '0.25rem', textAlign: 'center', fontSize: '0.6875rem', fontWeight: '800',
                      color: tColor, ...borderL,
                    }}>
                      {strokePlayTeamScores.overall[teamIdx] || '–'}
                    </td>
                  </tr>
                )}

                {/* Stroke play diff rows (FRONT/BACK + TOTAL) — after Team A only */}
                {teamIdx === 0 && strokePlayTeamScores?.diffs && (
                  <>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: 'var(--text-secondary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                        backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                      }}>
                        Front/Back
                      </td>
                      {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                        const diff = strokePlayTeamScores.diffs.frontBackDiff[hole]
                        const fmt = formatStrokeDiff(diff)
                        return (
                          <React.Fragment key={hole}>
                            {numHoles === 18 && hole === 10 && (() => {
                              const fDiff = (strokePlayTeamScores.front[0] - strokePlayTeamScores.front[1]) * strokePlayTeamScores.diffs.sign
                              const fFmt = formatStrokeDiff(fDiff)
                              return (
                                <td style={{
                                  padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                                  color: fFmt?.color || 'var(--text-muted)', ...borderL,
                                  ...(strokePlayTeamScores.frontWinner != null ? { boxShadow: `inset 0 0 0 2px ${fFmt?.color}` } : {}),
                                }}>
                                  {fFmt?.text || '–'}
                                </td>
                              )
                            })()}
                            <td style={{
                              padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                              color: fmt?.color || 'transparent',
                              ...(numHoles === 18 && hole === 10 ? borderL : {}),
                            }}>
                              {fmt?.text || ''}
                            </td>
                          </React.Fragment>
                        )
                      })}
                      {numHoles === 18 && (() => {
                        const bDiff = (strokePlayTeamScores.back[0] - strokePlayTeamScores.back[1]) * strokePlayTeamScores.diffs.sign
                        const bFmt = formatStrokeDiff(bDiff)
                        return (
                          <td style={{
                            padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800',
                            color: bFmt?.color || 'var(--text-muted)', ...borderL,
                            ...(strokePlayTeamScores.backWinner != null ? { boxShadow: `inset 0 0 0 2px ${bFmt?.color}` } : {}),
                          }}>
                            {bFmt?.text || '–'}
                          </td>
                        )
                      })()}
                      <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                    </tr>

                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: 'var(--text-secondary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                        backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                      }}>
                        Total
                      </td>
                      {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                        const diff = strokePlayTeamScores.diffs.overallDiff[hole]
                        const fmt = formatStrokeDiff(diff)
                        return (
                          <React.Fragment key={hole}>
                            {numHoles === 18 && hole === 10 && (
                              <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                            )}
                            <td style={{
                              padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                              color: fmt?.color || 'transparent',
                              ...(numHoles === 18 && hole === 10 ? borderL : {}),
                            }}>
                              {fmt?.text || ''}
                            </td>
                          </React.Fragment>
                        )
                      })}
                      {numHoles === 18 && <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />}
                      {(() => {
                        const oDiff = (strokePlayTeamScores.overall[0] - strokePlayTeamScores.overall[1]) * strokePlayTeamScores.diffs.sign
                        const oFmt = formatStrokeDiff(oDiff)
                        return (
                          <td style={{
                            padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800',
                            color: oFmt?.color || 'var(--text-muted)', ...borderL,
                            ...(strokePlayTeamScores.overallWinner != null ? { boxShadow: `inset 0 0 0 2px ${oFmt?.color}` } : {}),
                          }}>
                            {oFmt?.text || '–'}
                          </td>
                        )
                      })()}
                    </tr>
                  </>
                )}

                {/* Match play + press rows — after first team, inside Team A's rowSpan */}
                {teamIdx === 0 && matchPlayHoleStatus && (
                  <>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: 'var(--text-secondary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                        backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                      }}>
                        Front/Back
                      </td>
                      {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                        const isFront = hole <= 9
                        const segStatus = isFront ? matchPlayHoleStatus.front : matchPlayHoleStatus.back
                        const clinch = isFront ? matchPlayHoleStatus.frontClinch : matchPlayHoleStatus.backClinch
                        const isClinchHole = clinch && hole === clinch.clinchHole
                        const isAfterClinch = clinch && hole > clinch.clinchHole
                        const clinchColor = clinch?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                        return (
                          <React.Fragment key={hole}>
                            {numHoles === 18 && hole === 10 && (() => {
                              const fc = matchPlayHoleStatus.frontClinch
                              if (fc) {
                                const c = fc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{fc.text}</td>
                              }
                              const f = formatMatchDiff(matchPlayHoleStatus.front[9])
                              return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: f?.color || 'var(--text-muted)', ...borderL }}>{f?.text || '–'}</td>
                            })()}
                            {isAfterClinch ? (
                              <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? borderL : {}) }} />
                            ) : isClinchHole ? (
                              <td style={{
                                padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                ...(numHoles === 18 && hole === 10 ? borderL : {}),
                              }}>
                                {clinch.text}
                              </td>
                            ) : (() => {
                              const fmt = formatMatchDiff(segStatus[hole])
                              return (
                                <td style={{
                                  padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                  color: fmt?.color || 'transparent',
                                  ...(numHoles === 18 && hole === 10 ? borderL : {}),
                                }}>
                                  {fmt?.text || ''}
                                </td>
                              )
                            })()}
                          </React.Fragment>
                        )
                      })}
                      {numHoles === 18 && (() => {
                        const bc = matchPlayHoleStatus.backClinch
                        if (bc) {
                          const c = bc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                          return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{bc.text}</td>
                        }
                        const b = formatMatchDiff(matchPlayHoleStatus.back[18])
                        return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: b?.color || 'var(--text-muted)', ...borderL }}>{b?.text || '–'}</td>
                      })()}
                      <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                    </tr>

                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.625rem', color: 'var(--text-secondary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                        backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                      }}>
                        Total
                      </td>
                      {Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                        const oc = matchPlayHoleStatus.overallClinch
                        const isClinchHole = oc && hole === oc.clinchHole
                        const isAfterClinch = oc && hole > oc.clinchHole
                        const clinchColor = oc?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                        return (
                          <React.Fragment key={hole}>
                            {numHoles === 18 && hole === 10 && (
                              <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                            )}
                            {isAfterClinch ? (
                              <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? borderL : {}) }} />
                            ) : isClinchHole ? (
                              <td style={{
                                padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                ...(numHoles === 18 && hole === 10 ? borderL : {}),
                              }}>
                                {oc.text}
                              </td>
                            ) : (() => {
                              const fmt = formatMatchDiff(matchPlayHoleStatus.overall[hole])
                              return (
                                <td style={{
                                  padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                  color: fmt?.color || 'transparent',
                                  ...(numHoles === 18 && hole === 10 ? borderL : {}),
                                }}>
                                  {fmt?.text || ''}
                                </td>
                              )
                            })()}
                          </React.Fragment>
                        )
                      })}
                      {numHoles === 18 && <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />}
                      {(() => {
                        const oc = matchPlayHoleStatus.overallClinch
                        if (oc) {
                          const c = oc.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                          return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{oc.text}</td>
                        }
                        const or = matchPlayHoleStatus.overallResult
                        if (or?.winner != null) {
                          const c = or.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                          const txt = teamAResultText(or) || '–'
                          return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{txt}</td>
                        }
                        const o = formatMatchDiff(matchPlayHoleStatus.overall[numHoles])
                        return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.625rem', fontWeight: '800', color: o?.color || 'var(--text-muted)', ...borderL }}>{o?.text || '–'}</td>
                      })()}
                    </tr>
                  </>
                )}
                {teamIdx === 0 && pressHoleStatuses.map(({ press, status, segment, startHole, clinch: pressClinch, segResult: pressResult, segLabel: customLabel }, pressIdx) => {
                  const segEnd = segment === 'front' ? 9 : 18
                  const segLabel = customLabel || (segment === 'front' ? 'Press F' : segment === 'back' ? 'Press B' : 'Press T')
                  return (
                    <tr key={press.id || pressIdx} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'rgba(239, 68, 68, 0.03)' }}>
                      <td style={{
                        padding: '0.375rem 0.5rem', fontWeight: '700', fontSize: '0.5625rem', color: 'var(--accent-danger, #ef4444)',
                        textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: hasTeams ? 22 : 0,
                        backgroundColor: 'var(--bg-secondary)', zIndex: 1, whiteSpace: 'nowrap',
                      }}>
                        {segLabel}
                      </td>
                      {(() => {
                        const pressSummaryCell = (() => {
                          if (pressClinch) {
                            const c = pressClinch.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                            return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{pressClinch.text}</td>
                          }
                          if (pressResult?.winner != null) {
                            const c = pressResult.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                            const txt = teamAResultText(pressResult) || '–'
                            return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{txt}</td>
                          }
                          const pf = isHighLow ? formatPointDiff(status[segEnd]) : formatMatchDiff(status[segEnd])
                          return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: pf?.color || 'var(--text-muted)', ...borderL }}>{pf?.text || '–'}</td>
                        })()
                        const isFrontPress = segment === 'front'
                        const isBackPress = segment === 'back'
                        return Array.from({ length: numHoles }, (_, i) => i + 1).map((hole) => {
                          const inRange = hole >= startHole && hole <= segEnd
                          const isClinchHole = pressClinch && hole === pressClinch.clinchHole
                          const isAfterClinch = pressClinch && hole > pressClinch.clinchHole && inRange
                          const clinchColor = pressClinch?.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                          return (
                            <React.Fragment key={hole}>
                              {numHoles === 18 && hole === 10 && (
                                isFrontPress ? pressSummaryCell : <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                              )}
                              {isAfterClinch ? (
                                <td style={{ padding: '0.25rem 0.125rem', ...(numHoles === 18 && hole === 10 ? borderL : {}) }} />
                              ) : isClinchHole ? (
                                <td style={{
                                  padding: '0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '800',
                                  color: clinchColor, boxShadow: `inset 0 0 0 2px ${clinchColor}`,
                                  ...(numHoles === 18 && hole === 10 ? borderL : {}),
                                }}>
                                  {pressClinch.text}
                                </td>
                              ) : (() => {
                                const fmt = inRange ? (isHighLow ? formatPointDiff(status[hole]) : formatMatchDiff(status[hole])) : null
                                return (
                                  <td style={{
                                    padding: '0.25rem 0.125rem', textAlign: 'center', fontSize: '0.5rem', fontWeight: '700',
                                    color: fmt?.color || 'transparent',
                                    ...(numHoles === 18 && hole === 10 ? borderL : {}),
                                  }}>
                                    {fmt?.text || ''}
                                  </td>
                                )
                              })()}
                            </React.Fragment>
                          )
                        })
                      })()}
                      {numHoles === 18 && (
                        segment === 'back'
                          ? (() => {
                              if (pressClinch) {
                                const c = pressClinch.teamAWon ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{pressClinch.text}</td>
                              }
                              if (pressResult?.winner != null) {
                                const c = pressResult.winner === 0 ? 'var(--accent-primary)' : 'var(--accent-danger, #ef4444)'
                                const txt = teamAResultText(pressResult) || '–'
                                return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: c, boxShadow: `inset 0 0 0 2px ${c}`, ...borderL }}>{txt}</td>
                              }
                              const pf = isHighLow ? formatPointDiff(status[segEnd]) : formatMatchDiff(status[segEnd])
                              return <td style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.5625rem', fontWeight: '800', color: pf?.color || 'var(--text-muted)', ...borderL }}>{pf?.text || '–'}</td>
                            })()
                          : <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                      )}
                      <td style={{ padding: '0.25rem', textAlign: 'center', ...borderL }} />
                    </tr>
                  )
                })}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
